// CASL ability factory for the feature-permission layer.
//
// Builds a CASL ability from a per-feature permission map (see userAccess.js):
//   { [Feature]: ['view', 'edit', ...] }
// A null/undefined map means "full access" — view + edit on every feature.
//
// Consumed via useAbility() in feature components, e.g.
//   ability.can('edit', 'DispatchBoard')

import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import { FEATURES, FEATURE_ACTIONS } from './features';

// Resolve a subject's type: an explicit tag, a Parse object's className, else the
// value itself (string feature subjects pass straight through). Lets callers also
// pass domain objects later, e.g. ability.can('edit', bookingRecord).
const detectSubjectType = (subject) => {
  if (subject && typeof subject === 'object') {
    return subject.__caslSubjectType__ || subject.className || subject.constructor?.name;
  }
  return subject;
};

export function defineAbilityFor(featurePermissions) {
  const { can, build } = new AbilityBuilder(createMongoAbility);

  if (featurePermissions) {
    // Listed user: grant exactly what's specified, per feature.
    Object.entries(featurePermissions).forEach(([feature, actions]) => {
      (Array.isArray(actions) ? actions : []).forEach((action) => can(action, feature));
    });
  } else {
    // Unlisted user: full access (view + edit on every feature).
    Object.values(FEATURES).forEach((feature) => {
      can(FEATURE_ACTIONS.VIEW, feature);
      can(FEATURE_ACTIONS.EDIT, feature);
    });
  }

  return build({ detectSubjectType });
}
